// The tenant's theme (E12-S1-T4, decided by D-32): branding is tenant data, served by
// GET /v1/branding — the product name, a logo and an allow-listed set of design-token overrides —
// and, here, the id of the surface theme those overrides sit on. The web app ships no tenant's
// theme: a theme is a token file in @waypoint/surface (src/styles/themes/), and everything else
// arrives as data.
//
// The record the client accepts (parsed strictly, like every answer):
//
//   { theme?: "neutral",                         a surface theme id; missing means the default
//     productName: "…",                          shown in the top bar in place of the default name
//     logoRef?: "data:image/svg+xml;base64,…",   an inline image, never an address: the logo is on
//                                                screen with the first frame and makes no request
//     tokens?: { "--wp-accent": "#3fcf8e", … } } overrides of the palette tokens in THEMEABLE_TOKENS,
//                                                as colours only, so a value cannot load anything
//
// A record that breaks any of this is refused whole: the default theme, name and logo, and a report.
// An unknown theme id gives the default theme (without the overrides, which were made for another
// ground) and a report; the tenant's name and logo still apply. No branding (404) is the default,
// with nothing to report.
//
// Applying is one function for every window of the app — the hub, and the pop-out Companion when
// it opens (it loads the same app, asks the same route and applies through the same function):
// the surface's applyTheme sets data-theme on the root, and the overrides are set on the root's
// own style, so the hub, a surface mounted in it and anything portalled to <body> all read them.
// Until a theme is applied the page is not shown (src/styles/app.css), so the first painted frame
// is the tenant's, never the default's.
import { applyTheme, DEFAULT_THEME, loggableThemeId, type ThemeId } from "@waypoint/surface";
import { z } from "zod";
import type { ApiErrorKind } from "../api/errors.js";

/**
 * The tokens a tenant may override: the agent surface's palette, nothing that sets type, shape,
 * motion or layers. GET /v1/branding refuses to save any other name (E04-S8-T4, D-32).
 */
export const THEMEABLE_TOKENS = [
  "--wp-bg",
  "--wp-bg-2",
  "--wp-panel",
  "--wp-panel-2",
  "--wp-line",
  "--wp-line-strong",
  "--wp-text",
  "--wp-text-2",
  "--wp-text-3",
  "--wp-accent",
  "--wp-accent-ink",
  "--wp-action",
  "--wp-user-bubble",
] as const;
export type ThemeableToken = (typeof THEMEABLE_TOKENS)[number];

/** A colour and nothing else: #rgb, #rgba, #rrggbb, #rrggbbaa, or rgb()/rgba() of numbers. */
const COLOUR = /^(?:#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})|rgba?\(\s*[0-9.%\s,/]+\))$/;

/** An inline logo: SVG, PNG or WebP as base64. 64 KiB of text at most. */
const LOGO = /^data:image\/(?:svg\+xml|png|webp);base64,[A-Za-z0-9+/]+={0,2}$/;

export const Branding = z.strictObject({
  theme: z
    .string()
    .regex(/^[a-z][a-z0-9-]{0,39}$/)
    .optional(),
  productName: z.string().trim().min(1).max(60),
  logoRef: z.string().max(65_536).regex(LOGO).optional(),
  tokens: z.partialRecord(z.enum(THEMEABLE_TOKENS), z.string().max(64).regex(COLOUR)).optional(),
});
export type Branding = z.output<typeof Branding>;

export const brandingPath = "/v1/branding";

/** What the app draws with: the surface theme, and the tenant's name and logo when it has them. */
export interface TenantTheme {
  readonly id: ThemeId;
  readonly productName?: string | undefined;
  readonly logoRef?: string | undefined;
  readonly tokens: Readonly<Partial<Record<ThemeableToken, string>>>;
}

export const DEFAULT_TENANT_THEME: TenantTheme = { id: DEFAULT_THEME, tokens: {} };

/** Something worth knowing about the theme. Ids and issue paths only: never a value or a body. */
export type ThemeReport =
  | { readonly code: "theme.unknown"; readonly requested: string }
  | { readonly code: "branding.invalid"; readonly issues: readonly string[] }
  | { readonly code: "branding.unavailable"; readonly reason: ApiErrorKind | "timeout" };

export type ReportTheme = (report: ThemeReport) => void;

/** The default reporter: one console warning, as the surface's applyTheme reports. */
export function warnTheme(report: ThemeReport): void {
  switch (report.code) {
    case "theme.unknown":
      console.warn(
        `[waypoint] theme "${loggableThemeId(report.requested)}" is not a theme the surface ships; using the default theme.`,
      );
      return;
    case "branding.invalid":
      console.warn(
        `[waypoint] the branding record was refused (${report.issues.join(", ")}); using the default theme.`,
      );
      return;
    case "branding.unavailable":
      console.warn(`[waypoint] no branding (${report.reason}); using the default theme.`);
  }
}

/**
 * Applies a branding record (or none) to `root` — the document element of the hub, or of the pop-out
 * Companion's window — and returns what was applied. The surface theme is set first; the overrides
 * only when that theme is the one the record named.
 */
export function applyTenantTheme(
  root: HTMLElement,
  branding: Branding | undefined,
  report: ReportTheme = warnTheme,
): TenantTheme {
  const id = applyTheme(root, branding?.theme, {
    onUnknown: (requested) => {
      report({ code: "theme.unknown", requested });
    },
  });
  const known = branding?.theme === undefined || id === branding.theme;
  const tokens = known ? (branding?.tokens ?? {}) : {};
  for (const name of THEMEABLE_TOKENS) {
    const value = tokens[name];
    if (value === undefined) root.style.removeProperty(name);
    else root.style.setProperty(name, value);
  }
  return { id, productName: branding?.productName, logoRef: branding?.logoRef, tokens };
}
